import { SHARE_ROLE_LABELS, type ShareRole, type User } from '@blueprint/domain'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '../../components/controls.js'
import { Dialog } from '../../components/Dialog.js'
import type { BoardShareEntry } from '../../data/index.js'
import { useBoardStore } from '../../state/board-store.js'
import { sessionRepository, useSessionStore } from '../../state/session-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from '../admin/AdminView.module.css'

/**
 * Sharing one board with colleagues in the same tenant. There is no way to share outside
 * it: the list of people offered here is the tenant's own, and the API refuses anyone
 * else even if this dialog asked.
 */
export function ShareBoardDialog() {
  const boardId = useUiStore((state) => state.sharingBoardId)
  const setSharingBoardId = useUiStore((state) => state.setSharingBoardId)
  const me = useSessionStore((state) => state.user)
  const boards = useBoardStore((state) => state.boards)
  const repository = sessionRepository()

  const [shares, setShares] = useState<BoardShareEntry[]>([])
  const [people, setPeople] = useState<User[]>([])
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState<ShareRole>('editor')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const board = boards.find((entry) => entry.id === boardId)

  const load = useCallback(async () => {
    if (!boardId) return
    try {
      const [currentShares, tenantUsers] = await Promise.all([
        repository.listShares(boardId),
        repository.listUsers(),
      ])
      setShares(currentShares)
      setPeople(tenantUsers)
      setError(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sharing could not be loaded.')
    }
  }, [boardId, repository])

  useEffect(() => {
    void load()
  }, [load])

  if (!boardId) return null

  const shared = new Set(shares.map((entry) => entry.userId))
  // Neither the owner nor someone who already has it needs offering again.
  const candidates = people.filter(
    (person) => person.id !== board?.ownerId && person.id !== me?.id && !shared.has(person.id),
  )

  const add = () => {
    if (!userId || busy) return
    setBusy(true)
    repository
      .shareBoard(boardId, userId, role)
      .then((next) => {
        setShares(next)
        setUserId('')
        setBusy(false)
      })
      .catch((caught: unknown) => {
        setBusy(false)
        setError(caught instanceof Error ? caught.message : 'That share could not be saved.')
      })
  }

  const change = (entry: BoardShareEntry, nextRole: ShareRole) => {
    void repository
      .shareBoard(boardId, entry.userId, nextRole)
      .then(setShares)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'That change could not be saved.'),
      )
  }

  const remove = (entry: BoardShareEntry) => {
    void repository
      .unshareBoard(boardId, entry.userId)
      .then(setShares)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'That share could not be removed.'),
      )
  }

  return (
    <Dialog
      open
      title={`Share "${board?.name ?? 'board'}"`}
      onClose={() => setSharingBoardId(null)}
      onSubmit={add}
      footer={<Button onClick={() => setSharingBoardId(null)}>Done</Button>}
    >
      <div className={styles.form}>
        <p className={styles.hint}>
          Only people in your tenant can be given access. Boards are never shared outside it.
        </p>

        <div className={styles.field}>
          <span className={styles.label}>Add someone</span>
          <div className={styles.actions} style={{ justifyContent: 'flex-start' }}>
            <select
              className={styles.input}
              aria-label="Colleague"
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
            >
              <option value="">Choose a colleague…</option>
              {candidates.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName ? `${person.displayName} (${person.email})` : person.email}
                </option>
              ))}
            </select>
            <select
              className={styles.input}
              aria-label="Access"
              value={role}
              onChange={(event) => setRole(event.target.value as ShareRole)}
            >
              <option value="editor">{SHARE_ROLE_LABELS.editor}</option>
              <option value="viewer">{SHARE_ROLE_LABELS.viewer}</option>
            </select>
            <Button type="submit" disabled={!userId || busy}>
              Share
            </Button>
          </div>
        </div>

        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Shared with</th>
              <th scope="col">Access</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shares.map((entry) => (
              <tr key={entry.userId} data-testid="share-row" data-share-email={entry.email}>
                <td className={styles.strong}>{entry.displayName || entry.email}</td>
                <td>
                  <select
                    className={styles.input}
                    aria-label={`Access for ${entry.email}`}
                    value={entry.role}
                    onChange={(event) => change(entry, event.target.value as ShareRole)}
                  >
                    <option value="editor">{SHARE_ROLE_LABELS.editor}</option>
                    <option value="viewer">{SHARE_ROLE_LABELS.viewer}</option>
                  </select>
                </td>
                <td className={styles.actions}>
                  <Button variant="danger" onClick={() => remove(entry)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            {shares.length === 0 ? (
              <tr>
                <td className={styles.empty} colSpan={3}>
                  Not shared with anyone yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  )
}
